/** @module @liquidefy/code-editor/project/project-detection @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  ProjectState,
  ProjectType,
  PackageManagerType,
  BuildSystem,
  TaskSource,
  ProjectTask,
  ProjectConfig,
  MonorepoConfig,
  MonorepoPackage,
  CIConfig,
  DependencyInfo,
  DependencyGraph,
  DependencyId,
  VulnerabilityInfo,
  EnvironmentState,
  EnvironmentId,
  TaskId,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asTaskId,
  asDependencyId,
  asEnvironmentId,
} from '../code-editor.js';

// ═══ §2 PROJECT DETECTION — type, package manager, build system, CI ═══

/**
 * Mapping of indicator files to project types.
 */
export const PROJECT_FILE_INDICATORS: Record<string, ProjectType> = {
  'package.json': 'single',
  'Cargo.toml': 'single',
  'go.mod': 'single',
  'pyproject.toml': 'single',
  'setup.py': 'single',
  'pom.xml': 'single',
  'build.gradle': 'single',
  'build.gradle.kts': 'single',
  'Gemfile': 'single',
  'composer.json': 'single',
  'mix.exs': 'single',
  'pubspec.yaml': 'single',
  'CMakeLists.txt': 'single',
  'Makefile': 'single',
  'Justfile': 'single',
  'Taskfile.yml': 'single',
  'Taskfile.yaml': 'single',
};

/**
 * Mapping of indicator files to package managers.
 */
export const PM_INDICATORS: Record<string, PackageManagerType> = {
  'package-lock.json': 'npm',
  'npm-shrinkwrap.json': 'npm',
  'yarn.lock': 'yarn',
  'pnpm-lock.yaml': 'pnpm',
  'bun.lockb': 'bun',
  'bun.lock': 'bun',
  'Cargo.lock': 'cargo',
  'Cargo.toml': 'cargo',
  'go.sum': 'go',
  'go.mod': 'go',
  'Pipfile.lock': 'pip',
  'requirements.txt': 'pip',
  'poetry.lock': 'poetry',
  'pyproject.toml': 'poetry',
  'build.gradle': 'gradle',
  'build.gradle.kts': 'gradle',
  'pom.xml': 'maven',
};

/**
 * Detect project configuration from a list of file paths.
 */
export function detectProject(files: string[]): Partial<ProjectState> {
  const type = detectProjectType(files);
  const packageManager = detectPackageManager(files);
  const buildSystem = detectBuildSystem(files);
  const ci = detectCIProvider(files);

  const config: ProjectConfig = {
    buildSystem: buildSystem ?? undefined,
    ci: ci ?? undefined,
  };

  return {
    type,
    packageManager,
    config,
  };
}

/**
 * Detect the project type from the file list.
 * Checks for monorepo indicators first, then falls back to single.
 */
export function detectProjectType(files: string[]): ProjectType {
  const names = new Set(files.map(f => extractFileName(f)));

  const hasWorkspaces = files.some(f =>
    f.endsWith('pnpm-workspace.yaml') ||
    f.endsWith('lerna.json') ||
    f.endsWith('nx.json') ||
    f.endsWith('turbo.json')
  );

  if (hasWorkspaces) return 'monorepo';

  const roots = files.filter(f => {
    const parts = f.split('/').filter(Boolean);
    return parts.length <= 2 && (
      f.endsWith('package.json') ||
      f.endsWith('Cargo.toml') ||
      f.endsWith('go.mod')
    );
  });

  if (roots.length > 1) return 'multi-root';

  return 'single';
}

/**
 * Detect the package manager from file indicators.
 */
export function detectPackageManager(files: string[]): PackageManagerType {
  const names = files.map(f => extractFileName(f));

  for (const name of names) {
    const pm = PM_INDICATORS[name];
    if (pm) return pm;
  }

  if (names.includes('package.json')) return 'npm';

  return 'unknown';
}

/**
 * Detect the build system from file indicators.
 */
export function detectBuildSystem(files: string[]): BuildSystem | null {
  const names = new Set(files.map(f => extractFileName(f)));

  if (names.has('vite.config.ts') || names.has('vite.config.js') || names.has('vite.config.mjs')) return 'vite';
  if (names.has('webpack.config.js') || names.has('webpack.config.ts') || names.has('webpack.config.mjs')) return 'webpack';
  if (names.has('esbuild.config.js') || names.has('esbuild.config.ts') || names.has('esbuild.mjs')) return 'esbuild';
  if (names.has('rollup.config.js') || names.has('rollup.config.ts') || names.has('rollup.config.mjs')) return 'rollup';
  if (names.has('turbopack.config.js')) return 'turbopack';
  if (names.has('rspack.config.js') || names.has('rspack.config.ts')) return 'rspack';
  if (names.has('.parcelrc') || names.has('parcel.config.js')) return 'parcel';
  if (names.has('tsconfig.json') && !names.has('vite.config.ts') && !names.has('webpack.config.js')) return 'tsc';
  if (names.has('.swcrc') || names.has('swc.config.js')) return 'swc';

  return null;
}

/**
 * Detect CI/CD provider from configuration files.
 */
export function detectCIProvider(files: string[]): CIConfig | null {
  for (const file of files) {
    if (file.includes('.github/workflows/')) {
      return { provider: 'github-actions', configPath: file };
    }
    if (file.endsWith('.gitlab-ci.yml') || file.endsWith('.gitlab-ci.yaml')) {
      return { provider: 'gitlab-ci', configPath: file };
    }
    if (file.endsWith('Jenkinsfile') || file.endsWith('jenkins.yml')) {
      return { provider: 'jenkins', configPath: file };
    }
    if (file.endsWith('.circleci/config.yml') || file.endsWith('.circleci/config.yaml')) {
      return { provider: 'circleci', configPath: file };
    }
    if (file.endsWith('.travis.yml')) {
      return { provider: 'travis', configPath: file };
    }
    if (file.endsWith('azure-pipelines.yml') || file.endsWith('azure-pipelines.yaml')) {
      return { provider: 'azure-pipelines', configPath: file };
    }
  }

  return null;
}

function extractFileName(path: string): string {
  const parts = path.split('/');
  return parts[parts.length - 1] || '';
}


// ═══ §3 TASK DISCOVERY — parse scripts from various build systems ═══

/**
 * Discover tasks from project files. Examines package.json scripts,
 * Makefile targets, Taskfile targets, etc.
 */
export function discoverTasks(
  files: Array<{ path: string; content: string }>,
  packageJson?: Record<string, unknown>
): ProjectTask[] {
  const tasks: ProjectTask[] = [];

  if (packageJson) {
    tasks.push(...parsePackageJsonScripts(packageJson));
  }

  for (const file of files) {
    const name = extractFileName(file.path);

    if (name === 'Makefile' || name === 'makefile' || name === 'GNUmakefile') {
      tasks.push(...parseMakefileTargets(file.content));
    }
    if (name === 'Taskfile.yml' || name === 'Taskfile.yaml') {
      tasks.push(...parseTaskfileTargets(file.content));
    }
    if (name === 'Justfile' || name === 'justfile') {
      tasks.push(...parseJustfileTargets(file.content));
    }
    if (name === 'Cargo.toml') {
      tasks.push(...parseCargoTargets(file.content));
    }
    if (name === 'build.gradle' || name === 'build.gradle.kts') {
      tasks.push(...parseGradleTargets(file.content));
    }
  }

  return tasks;
}

/**
 * Parse scripts from a package.json object.
 */
export function parsePackageJsonScripts(packageJson: Record<string, unknown>): ProjectTask[] {
  const scripts = packageJson['scripts'] as Record<string, string> | undefined;
  if (!scripts || typeof scripts !== 'object') return [];

  return Object.entries(scripts).map(([name, command]) => ({
    id: asTaskId(generateId('task')),
    name,
    source: 'package.json' as TaskSource,
    command: typeof command === 'string' ? command : String(command),
    cwd: '.',
    dependencies: [],
    status: 'idle' as const,
  }));
}

/**
 * Parse targets from a Makefile.
 */
export function parseMakefileTargets(content: string): ProjectTask[] {
  const tasks: ProjectTask[] = [];
  const lines = content.split('\n');

  for (const line of lines) {
    const match = line.match(/^([a-zA-Z_][\w-]*)\s*:/);
    if (match && !line.startsWith('\t') && !line.startsWith('  ')) {
      const name = match[1]!;
      if (name.startsWith('.') || name === 'PHONY') continue;

      tasks.push({
        id: asTaskId(generateId('task')),
        name,
        source: 'makefile',
        command: `make ${name}`,
        cwd: '.',
        dependencies: [],
        status: 'idle',
      });
    }
  }

  return tasks;
}

/**
 * Parse targets from a Taskfile (YAML-based).
 */
export function parseTaskfileTargets(content: string): ProjectTask[] {
  const tasks: ProjectTask[] = [];
  const lines = content.split('\n');

  let inTasks = false;
  let currentIndent = 0;

  for (const line of lines) {
    if (line.trimStart() === 'tasks:' || line.trimStart().startsWith('tasks:')) {
      inTasks = true;
      currentIndent = line.length - line.trimStart().length + 2;
      continue;
    }

    if (inTasks) {
      const trimmed = line.trimStart();
      const indent = line.length - trimmed.length;

      if (indent < currentIndent && trimmed.length > 0 && !trimmed.startsWith('#')) {
        if (!trimmed.startsWith(' ') && trimmed.includes(':') && !trimmed.startsWith('-')) {
          inTasks = false;
          continue;
        }
      }

      if (indent === currentIndent && trimmed.endsWith(':')) {
        const name = trimmed.slice(0, -1).trim();
        tasks.push({
          id: asTaskId(generateId('task')),
          name,
          source: 'taskfile',
          command: `task ${name}`,
          cwd: '.',
          dependencies: [],
          status: 'idle',
        });
      }
    }
  }

  return tasks;
}

/**
 * Parse recipes from a Justfile.
 */
export function parseJustfileTargets(content: string): ProjectTask[] {
  const tasks: ProjectTask[] = [];
  const lines = content.split('\n');

  for (const line of lines) {
    const match = line.match(/^([a-zA-Z_][\w-]*)\s*(?:\(.*\))?\s*:/);
    if (match) {
      const name = match[1]!;
      tasks.push({
        id: asTaskId(generateId('task')),
        name,
        source: 'custom',
        command: `just ${name}`,
        cwd: '.',
        dependencies: [],
        status: 'idle',
      });
    }
  }

  return tasks;
}

/**
 * Parse common Cargo targets from Cargo.toml.
 */
export function parseCargoTargets(content: string): ProjectTask[] {
  const targets = ['build', 'test', 'run', 'check', 'clippy', 'fmt', 'doc', 'bench'];
  const hasBin = content.includes('[[bin]]') || content.includes('[package]');
  const hasLib = content.includes('[lib]');

  const tasks: ProjectTask[] = targets
    .filter(t => {
      if (t === 'run' && !hasBin) return false;
      return true;
    })
    .map(name => ({
      id: asTaskId(generateId('task')),
      name,
      source: 'cargo' as TaskSource,
      command: `cargo ${name}`,
      cwd: '.',
      dependencies: [],
      status: 'idle' as const,
    }));

  return tasks;
}

/**
 * Parse tasks from a Gradle build file.
 */
export function parseGradleTargets(content: string): ProjectTask[] {
  const tasks: ProjectTask[] = [];
  const lines = content.split('\n');

  const defaultTasks = ['build', 'test', 'clean', 'assemble', 'check', 'jar'];
  for (const name of defaultTasks) {
    tasks.push({
      id: asTaskId(generateId('task')),
      name,
      source: 'gradle',
      command: `./gradlew ${name}`,
      cwd: '.',
      dependencies: [],
      status: 'idle',
    });
  }

  const taskRe = /task\s+(\w+)/;
  for (const line of lines) {
    const match = line.match(taskRe);
    if (match && !defaultTasks.includes(match[1]!)) {
      tasks.push({
        id: asTaskId(generateId('task')),
        name: match[1]!,
        source: 'gradle',
        command: `./gradlew ${match[1]!}`,
        cwd: '.',
        dependencies: [],
        status: 'idle',
      });
    }
  }

  return tasks;
}

/**
 * Generate a display-friendly name for a task.
 */
export function taskDisplayName(task: ProjectTask): string {
  return `${task.name} (${task.source})`;
}

/**
 * Infer the category of a task from its name.
 */
export function taskCategory(task: ProjectTask): 'build' | 'test' | 'lint' | 'start' | 'deploy' | 'other' {
  const name = task.name.toLowerCase();
  const cmd = task.command.toLowerCase();

  if (name === 'build' || name === 'compile' || cmd.includes('build') || cmd.includes('tsc')) return 'build';
  if (name === 'test' || name.startsWith('test:') || cmd.includes('jest') || cmd.includes('vitest') || cmd.includes('mocha') || cmd.includes('pytest')) return 'test';
  if (name === 'lint' || name.startsWith('lint:') || cmd.includes('eslint') || cmd.includes('prettier') || cmd.includes('clippy')) return 'lint';
  if (name === 'start' || name === 'dev' || name === 'serve' || name === 'run' || cmd.includes('dev')) return 'start';
  if (name === 'deploy' || name.startsWith('deploy:') || cmd.includes('deploy') || cmd.includes('publish')) return 'deploy';

  return 'other';
}


// ═══ §4 DEPENDENCY ANALYSIS — parse, graph, circular detection, topology ═══

/**
 * Parse dependencies from a package.json-style object.
 */
export function parseDependencies(
  packageJson: Record<string, unknown>,
  type: DependencyInfo['type']
): DependencyInfo[] {
  const key = type === 'production' ? 'dependencies'
    : type === 'development' ? 'devDependencies'
    : type === 'peer' ? 'peerDependencies'
    : 'optionalDependencies';

  const deps = packageJson[key] as Record<string, string> | undefined;
  if (!deps || typeof deps !== 'object') return [];

  return Object.entries(deps).map(([name, version]) => ({
    id: asDependencyId(generateId('dep')),
    name,
    version: typeof version === 'string' ? version : String(version),
    type,
    isDirectDep: true,
    license: undefined,
    vulnerabilities: [],
  }));
}

/**
 * Build a dependency graph from a list of dependency info entries.
 */
export function buildDependencyGraph(deps: DependencyInfo[]): DependencyGraph {
  const nodes = new Map<string, DependencyInfo>();
  const edges = new Map<string, string[]>();

  for (const dep of deps) {
    nodes.set(dep.name, dep);
    if (!edges.has(dep.name)) edges.set(dep.name, []);
  }

  const circularPaths = findCircularDeps({ nodes, edges, hasCircular: false, circularPaths: [] });

  return {
    nodes,
    edges,
    hasCircular: circularPaths.length > 0,
    circularPaths,
  };
}

/**
 * Find circular dependency paths in a graph.
 */
export function findCircularDeps(graph: DependencyGraph): string[][] {
  const circular: string[][] = [];
  const visited = new Set<string>();
  const stack = new Set<string>();

  function dfs(name: string, path: string[]): void {
    if (stack.has(name)) {
      const cycleStart = path.indexOf(name);
      if (cycleStart >= 0) {
        circular.push([...path.slice(cycleStart), name]);
      }
      return;
    }
    if (visited.has(name)) return;

    visited.add(name);
    stack.add(name);
    path.push(name);

    const neighbors = graph.edges.get(name) ?? [];
    for (const neighbor of neighbors) {
      dfs(neighbor, path);
    }

    path.pop();
    stack.delete(name);
  }

  for (const name of graph.nodes.keys()) {
    dfs(name, []);
  }

  return circular;
}

/**
 * Topologically sort the dependency graph.
 */
export function topologicalSort(graph: DependencyGraph): string[] {
  const inDegree = new Map<string, number>();
  const sorted: string[] = [];

  for (const name of graph.nodes.keys()) {
    if (!inDegree.has(name)) inDegree.set(name, 0);
  }

  for (const [, neighbors] of graph.edges) {
    for (const n of neighbors) {
      inDegree.set(n, (inDegree.get(n) ?? 0) + 1);
    }
  }

  const queue: string[] = [];
  for (const [name, degree] of inDegree) {
    if (degree === 0) queue.push(name);
  }

  while (queue.length > 0) {
    const current = queue.shift()!;
    sorted.push(current);

    const neighbors = graph.edges.get(current) ?? [];
    for (const n of neighbors) {
      const newDegree = (inDegree.get(n) ?? 1) - 1;
      inDegree.set(n, newDegree);
      if (newDegree === 0) queue.push(n);
    }
  }

  return sorted;
}

/**
 * Get direct dependencies of a named package.
 */
export function directDeps(graph: DependencyGraph, name: string): string[] {
  return graph.edges.get(name) ?? [];
}

/**
 * Get all transitive dependencies of a named package.
 */
export function transitiveDeps(graph: DependencyGraph, name: string): string[] {
  const result = new Set<string>();
  const queue = [...(graph.edges.get(name) ?? [])];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (result.has(current)) continue;
    result.add(current);
    const neighbors = graph.edges.get(current) ?? [];
    queue.push(...neighbors);
  }

  return Array.from(result);
}

/**
 * Compute the maximum dependency depth for a package.
 */
export function dependencyDepth(graph: DependencyGraph, name: string): number {
  const visited = new Set<string>();

  function maxDepth(current: string): number {
    if (visited.has(current)) return 0;
    visited.add(current);

    const neighbors = graph.edges.get(current) ?? [];
    if (neighbors.length === 0) return 0;

    let max = 0;
    for (const n of neighbors) {
      const d = maxDepth(n);
      if (d > max) max = d;
    }
    return max + 1;
  }

  return maxDepth(name);
}

/**
 * Check if a dependency is dev-only.
 */
export function isDevOnly(dep: DependencyInfo): boolean {
  return dep.type === 'development';
}

/**
 * Check if a dependency has any known vulnerabilities.
 */
export function hasVulnerabilities(dep: DependencyInfo): boolean {
  return dep.vulnerabilities.length > 0;
}


// ═══ §5 MONOREPO — detection, workspace parsing, affected packages ═══

/**
 * Detect the monorepo tool from file indicators.
 */
export function detectMonorepoTool(files: string[]): MonorepoConfig['tool'] | null {
  const names = new Set(files.map(f => extractFileName(f)));

  if (names.has('turbo.json')) return 'turborepo';
  if (names.has('nx.json')) return 'nx';
  if (names.has('lerna.json')) return 'lerna';
  if (names.has('pnpm-workspace.yaml')) return 'pnpm-workspaces';

  for (const file of files) {
    if (file.endsWith('package.json')) {
      return 'npm-workspaces';
    }
  }

  return null;
}

/**
 * Parse workspace patterns from package.json or tool config.
 */
export function parseWorkspaces(
  packageJson: Record<string, unknown>,
  tool: MonorepoConfig['tool']
): string[] {
  if (tool === 'npm-workspaces' || tool === 'yarn-workspaces') {
    const workspaces = packageJson['workspaces'];
    if (Array.isArray(workspaces)) return workspaces as string[];
    if (workspaces && typeof workspaces === 'object' && 'packages' in workspaces) {
      return (workspaces as Record<string, unknown>)['packages'] as string[] ?? [];
    }
    return [];
  }

  if (tool === 'pnpm-workspaces') {
    const packages = packageJson['packages'];
    if (Array.isArray(packages)) return packages as string[];
    return [];
  }

  return [];
}

/**
 * Discover packages from workspace glob patterns (simplified: just returns pattern-based paths).
 */
export function discoverPackages(workspaces: string[]): string[] {
  return workspaces.map(ws => ws.replace(/\/\*$/, '').replace(/\*\*$/, ''));
}

/**
 * Build a dependency matrix for monorepo packages.
 * Returns a map of package name → list of internal package names it depends on.
 */
export function packageDependencyMatrix(packages: MonorepoPackage[]): Map<string, string[]> {
  const names = new Set(packages.map(p => p.name));
  const matrix = new Map<string, string[]>();

  for (const pkg of packages) {
    const internalDeps = pkg.dependencies.filter(d => names.has(d));
    matrix.set(pkg.name, internalDeps);
  }

  return matrix;
}

/**
 * Compute the build order for monorepo packages (topological sort).
 */
export function buildOrder(packages: MonorepoPackage[]): string[] {
  const matrix = packageDependencyMatrix(packages);
  const inDegree = new Map<string, number>();
  const sorted: string[] = [];

  for (const pkg of packages) {
    inDegree.set(pkg.name, 0);
  }

  for (const [, deps] of matrix) {
    for (const dep of deps) {
      inDegree.set(dep, (inDegree.get(dep) ?? 0) + 1);
    }
  }

  const queue: string[] = [];
  for (const [name, degree] of inDegree) {
    if (degree === 0) queue.push(name);
  }

  while (queue.length > 0) {
    const current = queue.shift()!;
    sorted.push(current);

    const deps = matrix.get(current) ?? [];
    for (const dep of deps) {
      const newDegree = (inDegree.get(dep) ?? 1) - 1;
      inDegree.set(dep, newDegree);
      if (newDegree === 0) queue.push(dep);
    }
  }

  return sorted;
}

/**
 * Determine which packages are affected by a set of changed files.
 * A package is affected if any changed file is within its path.
 */
export function affectedPackages(packages: MonorepoPackage[], changedFiles: string[]): string[] {
  const affected = new Set<string>();
  const matrix = packageDependencyMatrix(packages);

  for (const pkg of packages) {
    const pkgPath = pkg.path.endsWith('/') ? pkg.path : pkg.path + '/';
    for (const file of changedFiles) {
      if (file.startsWith(pkgPath) || file === pkg.path) {
        affected.add(pkg.name);
        break;
      }
    }
  }

  let changed = true;
  while (changed) {
    changed = false;
    for (const [name, deps] of matrix) {
      if (affected.has(name)) continue;
      if (deps.some(d => affected.has(d))) {
        affected.add(name);
        changed = true;
      }
    }
  }

  return Array.from(affected);
}


// ═══ §6 ENVIRONMENT — .env parsing, secret detection, masking ═══

/**
 * Parse environment variables from a .env-style file.
 */
export function parseEnvFile(content: string): Record<string, string> {
  return parseDotEnv(content);
}

/**
 * Parse a dotenv-format string into key-value pairs.
 */
export function parseDotEnv(content: string): Record<string, string> {
  const vars: Record<string, string> = {};
  const lines = content.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;

    const eqIdx = trimmed.indexOf('=');
    if (eqIdx < 0) continue;

    const key = trimmed.slice(0, eqIdx).trim();
    let value = trimmed.slice(eqIdx + 1).trim();

    if ((value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }

    if (value.startsWith('"')) {
      value = value.replace(/\\n/g, '\n').replace(/\\"/g, '"');
    }

    if (key) vars[key] = value;
  }

  return vars;
}

/**
 * Parse Docker-style environment declarations (KEY=VALUE or KEY lines).
 */
export function parseDockerEnv(content: string): Record<string, string> {
  const vars: Record<string, string> = {};
  const lines = content.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;

    const envMatch = trimmed.match(/^(?:ENV|ARG)\s+(\w+)(?:\s*=\s*|\s+)(.*)$/);
    if (envMatch) {
      vars[envMatch[1]!] = envMatch[2]!.trim();
      continue;
    }

    const eqIdx = trimmed.indexOf('=');
    if (eqIdx > 0) {
      const key = trimmed.slice(0, eqIdx).trim();
      const value = trimmed.slice(eqIdx + 1).trim();
      if (/^\w+$/.test(key)) vars[key] = value;
    }
  }

  return vars;
}

/**
 * Regex patterns that indicate a variable likely holds a secret.
 */
export const SECRET_PATTERNS: readonly RegExp[] = [
  /API[_-]?KEY/i,
  /API[_-]?SECRET/i,
  /SECRET[_-]?KEY/i,
  /ACCESS[_-]?KEY/i,
  /ACCESS[_-]?TOKEN/i,
  /AUTH[_-]?TOKEN/i,
  /TOKEN/i,
  /PASSWORD/i,
  /PASSWD/i,
  /PRIVATE[_-]?KEY/i,
  /SIGNING[_-]?KEY/i,
  /ENCRYPTION[_-]?KEY/i,
  /DATABASE[_-]?URL/i,
  /DB[_-]?PASSWORD/i,
  /DB[_-]?PASS/i,
  /STRIPE[_-]?KEY/i,
  /AWS[_-]?SECRET/i,
  /GCP[_-]?KEY/i,
  /AZURE[_-]?KEY/i,
  /CLIENT[_-]?SECRET/i,
  /WEBHOOK[_-]?SECRET/i,
  /JWT[_-]?SECRET/i,
  /COOKIE[_-]?SECRET/i,
  /SESSION[_-]?SECRET/i,
  /SSH[_-]?KEY/i,
  /GPG[_-]?KEY/i,
  /CERT/i,
];

/**
 * Detect which variable names look like secrets.
 */
export function detectSecrets(variables: Record<string, string>): string[] {
  return Object.keys(variables).filter(key =>
    SECRET_PATTERNS.some(pattern => pattern.test(key))
  );
}

/**
 * Mask secret values in a variables map, replacing with '***'.
 */
export function maskSecrets(variables: Record<string, string>): Record<string, string> {
  const secrets = new Set(detectSecrets(variables));
  const masked: Record<string, string> = {};

  for (const [key, value] of Object.entries(variables)) {
    masked[key] = secrets.has(key) ? '***' : value;
  }

  return masked;
}

/**
 * Merge environment variable maps, with overrides taking precedence.
 */
export function mergeEnvironments(
  base: Record<string, string>,
  overrides: Record<string, string>
): Record<string, string> {
  return { ...base, ...overrides };
}

/**
 * Compute the diff between two environment variable maps.
 */
export function environmentDiff(
  oldVars: Record<string, string>,
  newVars: Record<string, string>
): { added: string[]; removed: string[]; changed: string[] } {
  const added: string[] = [];
  const removed: string[] = [];
  const changed: string[] = [];

  for (const key of Object.keys(newVars)) {
    if (!(key in oldVars)) {
      added.push(key);
    } else if (oldVars[key] !== newVars[key]) {
      changed.push(key);
    }
  }

  for (const key of Object.keys(oldVars)) {
    if (!(key in newVars)) {
      removed.push(key);
    }
  }

  return { added, removed, changed };
}
